import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, FormControlLabel, Grid, Stack, Switch, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";

import {
  bulkAddCoreNetworkCapabilities,
  bulkAssignCoreNetworkBadges,
  bulkAssignCoreNetworkMemberships,
  bulkImportCoreNetworkMembers,
  createCoreNetworkBadge,
  exportCoreNetworkCapabilitiesCsv,
  exportCoreNetworkCapacityCsv,
  exportCoreNetworkMembersCsv,
  getCoreNetworkAdminSettings,
  listCoreNetworkBadges,
  listCoreNetworkMemberships,
  listCoreNetworkSubscriptions,
  listCoreNetworkWorkRequests,
  seedCoreNetworkAdminDefaults,
  updateCoreNetworkAdminSettings,
  updateCoreNetworkBadge,
  updateCoreNetworkSubscription,
} from "../../../services/coreNetwork";
import { AuthContext } from "../../../helpers/AuthContext";
import { CORE_NETWORK_PERMISSIONS, hasCoreNetworkPermission } from "../../../helpers/coreNetworkPermissions";
import { emitCoreNetworkUiConfigUpdated } from "../../../helpers/coreNetworkUiConfig";
import {
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  JsonListChips,
  LoadingState,
  MetricCard,
  SectionCard,
  safeArray,
} from "./coreNetworkUiShared";
import CoreNetworkBadgePanel from "./components/CoreNetworkBadgePanel";
import CoreNetworkSubscriptionPanel from "./components/CoreNetworkSubscriptionPanel";

const DEFAULT_IMPORT_TEMPLATE = `displayName,email,companyName,memberType,networkTypes,membershipStatus,role,allowedRequestTypes,rateType,rateAmount,rateCurrency,capabilityType,capabilityGroup,badgeCodes
NorthLine Decorators,ops@northline.example,NorthLine,decorator,decoration,active,decorator,decoration-production,fixed,250,CAD,screen_print,decoration,verified|vetted
Studio Proof Co,proofs@studio.example,Studio Proof Co,designer,design,active,designer,artwork-proof|proof-mockup,hourly,65,CAD,artwork-proof,design,verified`;

function uniqueStrings(values = []) {
  return [...new Set((Array.isArray(values) ? values : [values]).map((value) => String(value || "").trim()).filter(Boolean))];
}

function toCsvList(text = "", separators = /[,\n|]/) {
  return uniqueStrings(String(text || "").split(separators));
}

function stringifyCsvList(values = []) {
  return uniqueStrings(values).join(", ");
}

function parseCsv(text = "") {
  const input = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rows = [];
  let current = "";
  let row = [];
  let inQuotes = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const next = input[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(current);
      current = "";
      continue;
    }

    if (char === "\n" && !inQuotes) {
      row.push(current);
      rows.push(row);
      row = [];
      current = "";
      continue;
    }

    current += char;
  }

  if (current.length || row.length) {
    row.push(current);
    rows.push(row);
  }

  const normalizedRows = rows
    .map((columns) => columns.map((column) => String(column || "").trim()))
    .filter((columns) => columns.some(Boolean));

  if (!normalizedRows.length) return [];
  const [headers, ...dataRows] = normalizedRows;
  return dataRows.map((dataRow) =>
    headers.reduce((accumulator, header, index) => {
      if (!header) return accumulator;
      accumulator[header] = dataRow[index] || "";
      return accumulator;
    }, {})
  );
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function buildImportRows(rows = []) {
  return rows.map((row) => ({
    displayName: row.displayName || row.name || "",
    email: row.email || "",
    phone: row.phone || "",
    companyName: row.companyName || row.company || "",
    memberType: row.memberType || "vendor",
    networkTypesJson: toCsvList(row.networkTypes || row.networkTypesJson || ""),
    timezone: row.timezone || "",
    country: row.country || "",
    globalStatus: row.globalStatus || "active",
    profileJson: row.profileSummary ? { summary: row.profileSummary } : null,
    skillsJson: toCsvList(row.skills || row.skillsJson || ""),
    membershipStatus: row.membershipStatus || row.status || "active",
    role: row.role || "",
    allowedRequestTypesJson: toCsvList(row.allowedRequestTypes || ""),
    visibility: row.visibility || "",
    rateJson: row.rateType || row.rateAmount
      ? {
          feeType: row.rateType || null,
          amount: row.rateAmount ? Number(row.rateAmount) : null,
          currency: row.rateCurrency || "CAD",
        }
      : null,
    capabilityType: row.capabilityType || "",
    capabilityGroup: row.capabilityGroup || "",
    equipmentJson: row.equipmentNotes ? { notes: row.equipmentNotes } : null,
    constraintsJson: row.constraintsNotes ? { notes: row.constraintsNotes } : null,
    badgeCodes: toCsvList(row.badgeCodes || ""),
  }));
}

const emptySettingsForm = {
  mvpTalentEnabled: false,
  workRequestsLabel: "Work Requests",
  talentLabel: "Talent",
  matchesEnabled: true,
  requestTypesText: "",
  capabilityGroupsText: "",
  decorationCapabilitiesText: "",
  defaultOfferExpiryHours: "72",
  capabilityFit: "35",
  capacityTimeline: "20",
  logistics: "15",
  rating: "15",
  priceBand: "10",
  tierWeight: "5",
};

export default function CoreNetworkSettings() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [success, setSuccess] = useState("");
  const [badges, setBadges] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [config, setConfig] = useState(null);
  const [settingsForm, setSettingsForm] = useState(emptySettingsForm);
  const [importCsvText, setImportCsvText] = useState(DEFAULT_IMPORT_TEMPLATE);
  const [importSummary, setImportSummary] = useState(null);
  const [summary, setSummary] = useState({
    providers: 0,
    activeProviders: 0,
    requests: 0,
    internalRequests: 0,
  });
  const canManageBadges = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_BADGES, instanceUniqueId);
  const canManageSubscriptions = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_SUBSCRIPTIONS, instanceUniqueId);

  const applySettingsToForm = useCallback((nextConfig) => {
    const settings = nextConfig?.settings || {};
    const weights = settings.matchingWeights || {};
    setSettingsForm({
      mvpTalentEnabled: settings.mvpTalentEnabled === true,
      workRequestsLabel: settings.workRequestsLabel || "Work Requests",
      talentLabel: settings.talentLabel || "Talent",
      matchesEnabled: settings.matchesEnabled !== false,
      requestTypesText: stringifyCsvList(settings.requestTypes || []),
      capabilityGroupsText: stringifyCsvList(settings.capabilityGroups || []),
      decorationCapabilitiesText: stringifyCsvList(settings.decorationCapabilities || []),
      defaultOfferExpiryHours: String(settings.defaultOfferExpiryHours ?? 72),
      capabilityFit: String(weights.capabilityFit ?? 35),
      capacityTimeline: String(weights.capacityTimeline ?? 20),
      logistics: String(weights.logistics ?? 15),
      rating: String(weights.rating ?? 15),
      priceBand: String(weights.priceBand ?? 10),
      tierWeight: String(weights.tierWeight ?? 5),
    });
  }, []);

  const loadSettingsSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [membershipResponse, workRequestResponse, badgeResponse, subscriptionResponse, configResponse] = await Promise.all([
        listCoreNetworkMemberships({ instanceUniqueId }),
        listCoreNetworkWorkRequests({ instanceUniqueId }),
        listCoreNetworkBadges({}),
        listCoreNetworkSubscriptions({ instanceUniqueId }),
        getCoreNetworkAdminSettings(instanceUniqueId),
      ]);
      const memberships = safeArray(membershipResponse.memberships);
      const requests = safeArray(workRequestResponse.workRequests);
      setBadges(safeArray(badgeResponse.badges));
      setSubscriptions(safeArray(subscriptionResponse.subscriptions));
      setConfig(configResponse.config || null);
      applySettingsToForm(configResponse.config || null);
      setSummary({
        providers: memberships.length,
        activeProviders: memberships.filter((membership) => String(membership.status || "").toLowerCase() === "active").length,
        requests: requests.length,
        internalRequests: requests.filter((request) => String(request.fulfillmentProvider || "").toLowerCase() === "internal").length,
      });
    } catch (loadError) {
      setError(
        loadError?.response?.data?.message ||
          loadError.message ||
          "Unable to load Core Network settings summary."
      );
    } finally {
      setLoading(false);
    }
  }, [applySettingsToForm, instanceUniqueId]);

  useEffect(() => {
    loadSettingsSummary();
  }, [loadSettingsSummary]);

  const withSaving = async (key, callback, successMessage = "") => {
    try {
      setSaving(key);
      setError("");
      setSuccess("");
      const result = await callback();
      await loadSettingsSummary();
      if (successMessage) setSuccess(successMessage);
      return result;
    } catch (actionError) {
      setError(actionError?.response?.data?.message || actionError.message || "Save failed.");
    } finally {
      setSaving("");
    }
  };

  const importPreviewCount = useMemo(() => parseCsv(importCsvText).length, [importCsvText]);

  const handleSettingsField = (field) => (event) => {
    setSettingsForm((prev) => ({
      ...prev,
      [field]: event?.target?.type === "checkbox" ? event.target.checked : event.target.value,
    }));
  };

  const handleSaveAdminSettings = async () => {
    const response = await withSaving(
      "admin-settings",
      () =>
        updateCoreNetworkAdminSettings({
          instanceUniqueId,
          mvpTalentEnabled: settingsForm.mvpTalentEnabled === true,
          workRequestsLabel: settingsForm.workRequestsLabel,
          talentLabel: settingsForm.talentLabel,
          matchesEnabled: settingsForm.matchesEnabled === true,
          requestTypes: toCsvList(settingsForm.requestTypesText),
          capabilityGroups: toCsvList(settingsForm.capabilityGroupsText),
          decorationCapabilities: toCsvList(settingsForm.decorationCapabilitiesText),
          defaultOfferExpiryHours: Number(settingsForm.defaultOfferExpiryHours || 72),
          matchingWeights: {
            capabilityFit: Number(settingsForm.capabilityFit || 35),
            capacityTimeline: Number(settingsForm.capacityTimeline || 20),
            logistics: Number(settingsForm.logistics || 15),
            rating: Number(settingsForm.rating || 15),
            priceBand: Number(settingsForm.priceBand || 10),
            tierWeight: Number(settingsForm.tierWeight || 5),
          },
        }),
      "Core Network admin settings saved."
    );
    emitCoreNetworkUiConfigUpdated(response?.config?.settings || null);
  };

  const handleSeedDefaults = async () => {
    await withSaving(
      "seed-defaults",
      () => seedCoreNetworkAdminDefaults(instanceUniqueId),
      "Default request types, capability groups, decoration capabilities, and badges seeded."
    );
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setImportCsvText(text);
      setSuccess(`Loaded ${file.name} for provider import review.`);
      setError("");
    } catch (_error) {
      setError("Unable to read the selected CSV file.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleBulkImportProviders = async () => {
    const parsedRows = buildImportRows(parseCsv(importCsvText));
    if (!parsedRows.length) {
      setError("Add at least one CSV row before importing providers.");
      return;
    }

    await withSaving(
      "bulk-import",
      async () => {
        const importedMembersResponse = await bulkImportCoreNetworkMembers(
          parsedRows.map((row) => ({
            displayName: row.displayName,
            email: row.email,
            phone: row.phone,
            companyName: row.companyName,
            memberType: row.memberType,
            networkTypesJson: row.networkTypesJson,
            timezone: row.timezone,
            country: row.country,
            globalStatus: row.globalStatus,
            profileJson: row.profileJson,
            skillsJson: row.skillsJson,
          }))
        );

        const importedMembers = safeArray(importedMembersResponse.members);
        const membershipRows = [];
        const capabilityRows = [];
        const badgeRows = [];
        const badgeLookup = new Map(
          safeArray(badges).map((badge) => [String(badge.code || "").trim().toLowerCase(), badge])
        );

        parsedRows.forEach((row, index) => {
          const member = importedMembers[index];
          if (!member) return;

          membershipRows.push({
            memberId: member.id,
            status: row.membershipStatus || "active",
            role: row.role || null,
            allowedRequestTypesJson: row.allowedRequestTypesJson.length ? row.allowedRequestTypesJson : null,
            rateJson: row.rateJson,
            visibility: row.visibility || null,
          });

          if (row.capabilityType) {
            capabilityRows.push({
              memberId: member.id,
              capabilityType: row.capabilityType,
              capabilityGroup: row.capabilityGroup || null,
              equipmentJson: row.equipmentJson,
              constraintsJson: row.constraintsJson,
            });
          }

          row.badgeCodes.forEach((badgeCode) => {
            const badge = badgeLookup.get(String(badgeCode || "").trim().toLowerCase());
            if (!badge) return;
            badgeRows.push({
              memberId: member.id,
              badgeId: badge.id,
            });
          });
        });

        const membershipResponse = membershipRows.length
          ? await bulkAssignCoreNetworkMemberships({
              instanceUniqueId,
              rows: membershipRows,
            })
          : { memberships: [] };
        const memberships = safeArray(membershipResponse.memberships);
        const membershipByMemberId = new Map(memberships.map((membership) => [Number(membership.memberId), membership]));

        if (capabilityRows.length) {
          await bulkAddCoreNetworkCapabilities({
            instanceUniqueId,
            rows: capabilityRows.map((row) => ({
              ...row,
              membershipId: membershipByMemberId.get(Number(row.memberId))?.id || null,
            })),
          });
        }

        if (badgeRows.length) {
          await bulkAssignCoreNetworkBadges({
            instanceUniqueId,
            rows: badgeRows.map((row) => ({
              ...row,
              membershipId: membershipByMemberId.get(Number(row.memberId))?.id || null,
            })),
          });
        }

        setImportSummary({
          importedMembers: importedMembers.length,
          assignedMemberships: memberships.length,
          addedCapabilities: capabilityRows.length,
          assignedBadges: badgeRows.length,
        });
      },
      "Provider CSV imported into the managed Core Network."
    );
  };

  const handleExport = async (type) => {
    try {
      setSaving(type);
      setError("");
      const exporters = {
        "export-members": () => exportCoreNetworkMembersCsv(instanceUniqueId),
        "export-capabilities": () => exportCoreNetworkCapabilitiesCsv(instanceUniqueId),
        "export-capacity": () => exportCoreNetworkCapacityCsv(instanceUniqueId),
      };
      const filenames = {
        "export-members": `core-network-members-${instanceUniqueId}.csv`,
        "export-capabilities": `core-network-capabilities-${instanceUniqueId}.csv`,
        "export-capacity": `core-network-capacity-${instanceUniqueId}.csv`,
      };
      const blob = await exporters[type]();
      downloadBlob(blob, filenames[type]);
      setSuccess("CSV export downloaded.");
    } catch (actionError) {
      setError(actionError?.response?.data?.message || actionError.message || "Export failed.");
    } finally {
      setSaving("");
    }
  };

  return (
    <CoreNetworkShell
      title="Core Network Settings"
      breadcrumb={`${CORE_NETWORK_LABEL} / Settings`}
    >
      {loading ? <LoadingState label="Loading Core Network settings..." /> : null}
      {!loading && error ? <ErrorState message={error} onAction={loadSettingsSummary} /> : null}
      {!loading && !error ? (
        <Stack spacing={3}>
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={3}><MetricCard label="Memberships" value={summary.providers} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Active providers" value={summary.activeProviders} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Tracked requests" value={summary.requests} /></Grid>
            <Grid item xs={12} md={3}><MetricCard label="Internal-provider requests" value={summary.internalRequests} /></Grid>
          </Grid>

          <SectionCard
            title="Cold-Start Strategy"
            subtitle="Curate the supply side first so Elite can manually route work before public marketplace rollout."
            action={
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" disabled={saving === "seed-defaults"} onClick={handleSeedDefaults}>
                  {saving === "seed-defaults" ? "Seeding..." : "Seed defaults"}
                </Button>
                <Button variant="contained" disabled={saving === "admin-settings"} onClick={handleSaveAdminSettings}>
                  {saving === "admin-settings" ? "Saving..." : "Save admin settings"}
                </Button>
              </Stack>
            }
          >
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.mvpTalentEnabled === true}
                    onChange={handleSettingsField("mvpTalentEnabled")}
                  />
                }
                label="Enable Core Network for this instance"
              />
              <Typography variant="body2">
                This is the internal managed-network setup layer. Use it to manually add providers, assign memberships, seed capabilities, and tune matching before any public provider signup exists.
              </Typography>
              <Typography variant="body2">
                Matching and offer defaults are stored per instance and continue to resolve through durable <strong>instanceId</strong> on the backend.
              </Typography>
            </Stack>
          </SectionCard>

          <SectionCard title="Navigation Labels" subtitle="Small instance-specific labels for the Core Network sidebar and tabs.">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Work Requests label"
                  value={settingsForm.workRequestsLabel}
                  onChange={handleSettingsField("workRequestsLabel")}
                  helperText="Blank values fall back to Work Requests."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Talent label"
                  value={settingsForm.talentLabel}
                  onChange={handleSettingsField("talentLabel")}
                  helperText="Blank values fall back to Talent."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settingsForm.matchesEnabled === true}
                      onChange={handleSettingsField("matchesEnabled")}
                    />
                  }
                  label="Enable Matches"
                />
              </Grid>
            </Grid>
          </SectionCard>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <SectionCard title="Request Types" subtitle="Allowed work categories for this instance's managed network.">
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Default request types"
                    helperText="Comma-separated. These seed membership allowed request types and admin workflows."
                    value={settingsForm.requestTypesText}
                    onChange={handleSettingsField("requestTypesText")}
                  />
                  <JsonListChips values={toCsvList(settingsForm.requestTypesText)} emptyLabel="No request types configured" />
                </Stack>
              </SectionCard>
            </Grid>
            <Grid item xs={12} lg={6}>
              <SectionCard title="Offer Defaults" subtitle="Managed marketplace defaults for Core Network offer timing and matching bias.">
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Default offer expiry (hours)"
                    value={settingsForm.defaultOfferExpiryHours}
                    onChange={handleSettingsField("defaultOfferExpiryHours")}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Current instance default: {config?.settings?.defaultOfferExpiryHours ?? 72} hours
                  </Typography>
                </Stack>
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <SectionCard title="Capability Groups" subtitle="Managed taxonomy for Core Network supply-side setup and filtering.">
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Capability groups"
                    helperText="Comma-separated default groups such as design, decoration, logistics, partner, software, and data entry."
                    value={settingsForm.capabilityGroupsText}
                    onChange={handleSettingsField("capabilityGroupsText")}
                  />
                  <JsonListChips values={toCsvList(settingsForm.capabilityGroupsText)} emptyLabel="No capability groups configured" />
                </Stack>
              </SectionCard>
            </Grid>
            <Grid item xs={12} lg={6}>
              <SectionCard title="Decoration Capabilities" subtitle="Starter decorator methods for the curated Core Network.">
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Decoration capabilities"
                    helperText="Comma-separated methods such as embroidery, screen print, DTF, heat press, and fulfillment."
                    value={settingsForm.decorationCapabilitiesText}
                    onChange={handleSettingsField("decorationCapabilitiesText")}
                  />
                  <JsonListChips values={toCsvList(settingsForm.decorationCapabilitiesText)} emptyLabel="No decoration capabilities configured" />
                </Stack>
              </SectionCard>
            </Grid>
          </Grid>

          <SectionCard title="Default Matching Weights" subtitle="Per-instance override for Core Network ranking before public marketplace launch.">
            <Grid container spacing={2}>
              {[
                ["capabilityFit", "Capability fit"],
                ["capacityTimeline", "Capacity / timeline"],
                ["logistics", "Logistics"],
                ["rating", "Rating"],
                ["priceBand", "Price band"],
                ["tierWeight", "Tier weight"],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={6} md={4} key={field}>
                  <TextField
                    fullWidth
                    type="number"
                    label={label}
                    value={settingsForm[field]}
                    onChange={handleSettingsField(field)}
                  />
                </Grid>
              ))}
            </Grid>
          </SectionCard>

          <SectionCard
            title="Provider Import / Export"
            subtitle="Manually seed the initial Core Network from CSV, then export current providers, capabilities, or capacity for review."
            action={
              <Stack direction="row" spacing={1}>
                <input ref={fileInputRef} hidden type="file" accept=".csv,text/csv" onChange={handleImportFile} />
                <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                  Import Providers CSV
                </Button>
                <Button variant="contained" disabled={saving === "bulk-import"} onClick={handleBulkImportProviders}>
                  {saving === "bulk-import" ? "Importing..." : "Run import"}
                </Button>
              </Stack>
            }
          >
            <Stack spacing={2}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Provider CSV"
                helperText="Supported columns include displayName, email, companyName, memberType, networkTypes, membershipStatus, role, allowedRequestTypes, rateType, rateAmount, rateCurrency, capabilityType, capabilityGroup, and badgeCodes."
                value={importCsvText}
                onChange={(event) => setImportCsvText(event.target.value)}
              />
              <Typography variant="body2" color="text.secondary">
                Preview rows: {importPreviewCount}
              </Typography>
              {importSummary ? (
                <Alert severity="info">
                  Imported {importSummary.importedMembers} members, assigned {importSummary.assignedMemberships} memberships, added {importSummary.addedCapabilities} capabilities, and assigned {importSummary.assignedBadges} badges.
                </Alert>
              ) : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" disabled={saving === "export-members"} onClick={() => handleExport("export-members")}>
                  {saving === "export-members" ? "Exporting..." : "Export Members CSV"}
                </Button>
                <Button variant="outlined" disabled={saving === "export-capabilities"} onClick={() => handleExport("export-capabilities")}>
                  {saving === "export-capabilities" ? "Exporting..." : "Export Capabilities CSV"}
                </Button>
                <Button variant="outlined" disabled={saving === "export-capacity"} onClick={() => handleExport("export-capacity")}>
                  {saving === "export-capacity" ? "Exporting..." : "Export Capacity CSV"}
                </Button>
              </Stack>
            </Stack>
          </SectionCard>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <CoreNetworkBadgePanel
                badges={badges}
                memberBadges={[]}
                saving={saving === "badge"}
                canManage={canManageBadges}
                onCreateBadge={(payload) => withSaving("badge", () => createCoreNetworkBadge(payload), "Badge definition created.")}
                onUpdateBadge={(badgeId, payload) => withSaving("badge", () => updateCoreNetworkBadge(badgeId, payload), "Badge definition updated.")}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <CoreNetworkSubscriptionPanel
                subscriptions={subscriptions}
                saving={saving === "subscription"}
                canManage={canManageSubscriptions}
                onUpdateSubscription={(subscriptionId, payload) => withSaving("subscription", () => updateCoreNetworkSubscription(subscriptionId, payload), "Subscription updated.")}
              />
            </Grid>
          </Grid>
        </Stack>
      ) : null}
    </CoreNetworkShell>
  );
}
